/*
 * ld-delivery-estimate.js
 * Replaces the server-rendered fallback line of <ld-delivery-estimate> with an honest
 * delivery window in French ("livraison estimée entre le mardi 6 et le jeudi 8 octobre").
 * All rules come from merchant settings (data-* attributes). No countdown, no timers
 * shown to the customer, no invented stock or urgency. If anything fails, the fallback stays.
 */
(function () {
  'use strict';

  if (!('customElements' in window) || customElements.get('ld-delivery-estimate')) return;

  var DAY_MS = 86400000;
  var MAX_STEPS = 370;
  var WEEKDAYS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
  var MONTHS = [
    'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
  ];
  var holidayCache = {};

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  // All dates are "calendar dates" stored as UTC midnight, so DST never shifts a day.
  function utcDate(y, m, d) {
    return new Date(Date.UTC(y, m, d));
  }

  function addDays(date, n) {
    return new Date(date.getTime() + n * DAY_MS);
  }

  function isoKey(date) {
    return date.getUTCFullYear() + '-' + pad(date.getUTCMonth() + 1) + '-' + pad(date.getUTCDate());
  }

  function toInt(value, fallback, min, max) {
    var n = parseInt(value, 10);
    if (isNaN(n)) n = fallback;
    if (typeof min === 'number' && n < min) n = min;
    if (typeof max === 'number' && n > max) n = max;
    return n;
  }

  // Anonymous Gregorian algorithm (Meeus/Jones/Butcher).
  function easterSunday(y) {
    var a = y % 19;
    var b = Math.floor(y / 100);
    var c = y % 100;
    var d = Math.floor(b / 4);
    var e = b % 4;
    var f = Math.floor((b + 8) / 25);
    var g = Math.floor((b - f + 1) / 3);
    var h = (19 * a + b - d - g + 15) % 30;
    var i = Math.floor(c / 4);
    var k = c % 4;
    var l = (32 + 2 * e + 2 * i - h - k) % 7;
    var m = Math.floor((a + 11 * h + 22 * l) / 451);
    var month = Math.floor((h + l - 7 * m + 114) / 31);
    var day = ((h + l - 7 * m + 114) % 31) + 1;
    return utcDate(y, month - 1, day);
  }

  // French public holidays (jours fériés, metropolitan France).
  function frenchHolidays(y) {
    if (holidayCache[y]) return holidayCache[y];
    var easter = easterSunday(y);
    var days = [
      utcDate(y, 0, 1), // Jour de l'an
      addDays(easter, 1), // Lundi de Pâques
      utcDate(y, 4, 1), // Fête du Travail
      utcDate(y, 4, 8), // Victoire 1945
      addDays(easter, 39), // Ascension
      addDays(easter, 50), // Lundi de Pentecôte
      utcDate(y, 6, 14), // Fête nationale
      utcDate(y, 7, 15), // Assomption
      utcDate(y, 10, 1), // Toussaint
      utcDate(y, 10, 11), // Armistice
      utcDate(y, 11, 25) // Noël
    ];
    var set = {};
    for (var n = 0; n < days.length; n++) set[isoKey(days[n])] = true;
    holidayCache[y] = set;
    return set;
  }

  // Accepts YYYY-MM-DD, DD-MM-YYYY, DD/MM/YYYY, DD.MM.YYYY separated by new lines, commas or spaces.
  function parseExcluded(raw) {
    var set = {};
    var tokens = String(raw || '').split(/[\s,;]+/);
    for (var n = 0; n < tokens.length; n++) {
      var t = tokens[n];
      var m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
      if (m) {
        set[m[1] + '-' + pad(+m[2]) + '-' + pad(+m[3])] = true;
        continue;
      }
      m = t.match(/^(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{4})$/);
      if (m) set[m[3] + '-' + pad(+m[2]) + '-' + pad(+m[1])] = true;
    }
    return set;
  }

  // "Today" and the current hour in the shop's time zone (not the visitor's).
  function nowInZone(tz) {
    try {
      var parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: tz,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        hourCycle: 'h23'
      }).formatToParts(new Date());
      var v = {};
      for (var n = 0; n < parts.length; n++) v[parts[n].type] = parseInt(parts[n].value, 10);
      if (isNaN(v.year) || isNaN(v.month) || isNaN(v.day) || isNaN(v.hour)) throw new Error('parts');
      return { date: utcDate(v.year, v.month - 1, v.day), hour: v.hour % 24 };
    } catch (e) {
      if (tz !== 'Europe/Paris') return nowInZone('Europe/Paris');
      var local = new Date();
      return { date: utcDate(local.getFullYear(), local.getMonth(), local.getDate()), hour: local.getHours() };
    }
  }

  function formatFr(date) {
    var day = date.getUTCDate();
    return WEEKDAYS[date.getUTCDay()] + ' ' + day + ' ' + MONTHS[date.getUTCMonth()];
  }

  function computeWindow(opts) {
    function isWorkday(d) {
      var dow = d.getUTCDay();
      if (opts.skipWeekends && (dow === 0 || dow === 6)) return false;
      var key = isoKey(d);
      if (opts.excluded[key]) return false;
      if (opts.skipHolidays && frenchHolidays(d.getUTCFullYear())[key]) return false;
      return true;
    }

    function nextWorkday(d) {
      for (var step = 0; step < MAX_STEPS; step++) {
        d = addDays(d, 1);
        if (isWorkday(d)) return d;
      }
      return null;
    }

    var now = opts.now || nowInZone(opts.timezone);
    // Dispatch today only on a business day before the cut-off, otherwise the next business day.
    // countFromOrder: the window counts N business days after the order date itself
    // (wording « … jours ouvrés après votre commande »), with no cut-off shift.
    var dispatch = now.date;
    if (!opts.countFromOrder && (!isWorkday(dispatch) || now.hour >= opts.cutoff)) dispatch = nextWorkday(dispatch);
    if (!dispatch) return null;

    var first = dispatch;
    for (var a = 0; a < opts.min && first; a++) first = nextWorkday(first);
    var last = first;
    for (var b = opts.min; b < opts.max && last; b++) last = nextWorkday(last);
    if (!first || !last) return null;
    return { first: first, last: last };
  }

  // Shared, read-only, with the homepage countdown (assets/lph-home-countdown.js): the same
  // business-day, French public holiday and closed-day rules as the delivery estimate, so the
  // dispatch date on the homepage always matches the product page.
  window.ldDeliveryCalc = {
    utcDate: utcDate,
    addDays: addDays,
    isoKey: isoKey,
    frenchHolidays: frenchHolidays,
    parseExcluded: parseExcluded,
    nowInZone: nowInZone,
    formatFr: formatFr
  };

  class LdDeliveryEstimate extends HTMLElement {
    constructor() {
      super();
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
    }

    connectedCallback() {
      this.update();
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    disconnectedCallback() {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    // Recalculate when a tab is reopened later (e.g. after the cut-off or the next morning).
    onVisibilityChange() {
      if (document.visibilityState === 'visible') this.update();
    }

    update() {
      var target = this.querySelector('[data-ld-delivery-text]');
      if (!target) return;
      // Keep the server-rendered sentence, so a later recalculation (tab reopened the next day)
      // can go back to it when no computed sentence applies any more.
      if (this.serverText == null) this.serverText = target.textContent;
      var ds = this.dataset;
      var min = toInt(ds.min, 2, 0, 60);
      var max = toInt(ds.max, 4, 0, 60);
      var timezone = ds.timezone || 'Europe/Paris';
      var now = nowInZone(timezone);
      // Optional dated switch of the window (e.g. Golden Week: 10-18 until 9 October, 8-14 from 10 October).
      var switchDate = String(ds.switchDate || '').match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
      if (switchDate && isoKey(now.date) >= switchDate[1] + '-' + pad(+switchDate[2]) + '-' + pad(+switchDate[3])) {
        min = toInt(ds.switchMin, min, 0, 60);
        max = toInt(ds.switchMax, max, 0, 60);
      }
      if (max < min) max = min;

      var base = {
        min: min,
        max: max,
        now: now,
        cutoff: toInt(ds.cutoff, 14, 0, 24),
        timezone: timezone,
        skipWeekends: ds.skipWeekends !== 'false',
        skipHolidays: ds.skipHolidays !== 'false',
        countFromOrder: ds.countFrom === 'order',
        excluded: parseExcluded(ds.excluded)
      };
      var range;
      var delayed = false;
      try {
        range = computeWindow(base);
        // Supplier closed days (e.g. Golden Week): when they push back the window of an order
        // placed today, the dates include them and the "delayed" sentence replaces the fixed promise.
        if (range && ds.delayDates) {
          var closed = parseExcluded(ds.delayDates);
          var merged = {};
          var key;
          for (key in base.excluded) merged[key] = true;
          for (key in closed) merged[key] = true;
          var withClosed = computeWindow(Object.assign({}, base, { excluded: merged }));
          if (withClosed) {
            delayed = isoKey(withClosed.last) > isoKey(range.last);
            range = withClosed;
          }
        }
      } catch (e) {
        range = null;
      }
      if (!range) return;

      var same = isoKey(range.first) === isoKey(range.last);
      var template = same && ds.textSingle ? ds.textSingle : ds.textRange;
      if (delayed && ds.textDelayed) template = ds.textDelayed;
      if (!template || (template.indexOf('{date1}') === -1 && template.indexOf('{date2}') === -1)) {
        if (this.classList.contains('is-computed')) {
          target.textContent = this.serverText;
          this.classList.remove('is-computed');
        }
        return;
      }

      var bold = ds.bold !== 'false';
      var fragment = document.createDocumentFragment();
      var pieces = template.split(/(\{date1\}|\{date2\})/);
      for (var n = 0; n < pieces.length; n++) {
        var piece = pieces[n];
        if (piece === '{date1}' || piece === '{date2}') {
          var date = piece === '{date1}' ? range.first : range.last;
          var wrapper = document.createElement(bold ? 'strong' : 'span');
          var time = document.createElement('time');
          time.setAttribute('datetime', isoKey(date));
          time.textContent = formatFr(date);
          wrapper.appendChild(time);
          fragment.appendChild(wrapper);
        } else if (piece) {
          fragment.appendChild(document.createTextNode(piece));
        }
      }
      target.textContent = '';
      target.appendChild(fragment);
      this.classList.add('is-computed');
    }
  }

  customElements.define('ld-delivery-estimate', LdDeliveryEstimate);
})();
